import client from '../api/client';

const CLOUDINARY_UPLOAD_URL = 'https://api.cloudinary.com/v1_1';

/**
 * Get signed upload params from backend.
 * @param {string} context - One of: checkin, profile, workout, social, meal
 */
export async function getUploadSignature(context) {
  const res = await client.get(`/uploads/signature?context=${context}`);
  return res.data;
}

/**
 * Convert a base64 data URL to a Blob for upload.
 */
function dataUrlToBlob(dataUrl) {
  const [header, b64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)[1];
  const bytes = atob(b64);
  const arr = new Uint8Array(bytes.length);
  for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

/**
 * One upload attempt: fetch a fresh signature and POST to Cloudinary.
 * Rejects with an Error carrying `.status` (0 for a network-level error) so the
 * caller can decide whether the failure is transient and worth retrying.
 */
function attemptUpload(dataUrl, context, onProgress) {
  return getUploadSignature(context).then((sig) => {
    const blob = dataUrlToBlob(dataUrl);
    const formData = new FormData();
    formData.append('file', blob);
    formData.append('api_key', sig.api_key);
    formData.append('timestamp', sig.timestamp);
    formData.append('signature', sig.signature);
    formData.append('folder', sig.folder);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${CLOUDINARY_UPLOAD_URL}/${sig.cloud_name}/image/upload`);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText).secure_url);
        } else {
          const err = new Error(`Cloudinary upload failed: ${xhr.status}`);
          err.status = xhr.status;
          reject(err);
        }
      };

      xhr.onerror = () => {
        const err = new Error('Cloudinary upload network error');
        err.status = 0; // network-level failure (no HTTP response)
        reject(err);
      };
      xhr.send(formData);
    });
  });
}

// Retry only on transient failures: network errors (status 0), rate-limiting
// (429), and 5xx. A 4xx (bad signature, expired timestamp, forbidden) is
// permanent — retrying with a fresh signature won't help, so fail fast and let
// the caller fall back to base64.
function isTransient(err) {
  const s = err?.status;
  return s === 0 || s === 429 || (s >= 500 && s < 600);
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Upload an image directly to Cloudinary, retrying transient failures.
 *
 * Most observed base64 fallbacks were one-off transient failures (a single
 * upload in an otherwise-successful batch). One or two retries with backoff
 * eliminate nearly all of them before the caller resorts to storing base64.
 *
 * @param {string} dataUrl - Base64 data URL from ImageCropper
 * @param {string} context - Upload context (checkin, profile, etc.)
 * @param {function} [onProgress] - Progress callback (0-100)
 * @param {number} [retries=2] - Extra attempts after the first on transient errors
 * @returns {Promise<string>} Cloudinary secure URL
 */
export async function uploadToCloudinary(dataUrl, context, onProgress, retries = 2) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      if (onProgress) onProgress(0); // reset bar for each fresh attempt
      return await attemptUpload(dataUrl, context, onProgress);
    } catch (err) {
      lastErr = err;
      if (attempt < retries && isTransient(err)) {
        await sleep(500 * (attempt + 1)); // 500ms, then 1000ms
        continue;
      }
      throw err;
    }
  }
  throw lastErr;
}

/**
 * Append Cloudinary transform params to a URL for responsive thumbnails.
 * If the URL is not a Cloudinary URL (e.g. legacy base64), returns it unchanged.
 * @param {string} url - Cloudinary URL
 * @param {number} width - Target width in pixels
 * @returns {string} Transformed URL
 */
export function cloudinaryThumb(url, width = 300) {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  return url.replace('/upload/', `/upload/w_${width},q_auto,f_auto/`);
}

/**
 * Avatar-specific transform (square crop).
 */
export function cloudinaryAvatar(url, size = 80) {
  if (!url || !url.includes('res.cloudinary.com')) return url;
  return url.replace('/upload/', `/upload/w_${size},h_${size},c_fill,q_auto,f_auto/`);
}
